import React, { createContext, useContext, useState, useEffect } from 'react';

interface Location {
    city: string;
    state: string;
    locality?: string;
    lat?: number;
    lng?: number;
    formattedAddress?: string;
}

interface LocationContextType {
    location: Location | null;
    setLocation: (location: Location | null) => void;
    isLoading: boolean;
    detectLocation: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [location, setLocationState] = useState<Location | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Load saved location from localStorage on mount
    useEffect(() => {
        const savedLocation = localStorage.getItem('user_location');
        if (savedLocation) {
            try {
                setLocationState(JSON.parse(savedLocation));
            } catch (e) {
                console.error('Failed to parse saved location', e);
            }
        }
    }, []);

    const setLocation = (newLocation: Location | null) => {
        setLocationState(newLocation);
        if (newLocation) {
            localStorage.setItem('user_location', JSON.stringify(newLocation));
        } else {
            localStorage.removeItem('user_location');
        }
    };

    const detectLocation = async () => {
        setIsLoading(true);
        try {
            if (!navigator.geolocation) {
                throw new Error('Geolocation is not supported by your browser');
            }

            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject);
            });

            const { latitude, longitude } = position.coords;

            // Use Google Maps Geocoding API if available, otherwise fallback to mock or OpenStreetMap
            // For now, we'll simulate a successful detection with coordinates
            // In a real app, you'd call: https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=YOUR_API_KEY

            // Mock response for now as we don't have the API key in this context
            // The user asked to use Google Maps API, so we assume the script is loaded or will be.
            // We will try to use the Geocoder if available globally.

            if (window.google && window.google.maps && window.google.maps.Geocoder) {
                const geocoder = new window.google.maps.Geocoder();
                const response = await geocoder.geocode({ location: { lat: latitude, lng: longitude } });

                if (response.results[0]) {
                    const result = response.results[0];
                    const cityComponent = result.address_components.find(c => c.types.includes('locality'));
                    const stateComponent = result.address_components.find(c => c.types.includes('administrative_area_level_1'));
                    const localityComponent = result.address_components.find(c => c.types.includes('sublocality'));

                    const newLocation = {
                        city: cityComponent?.long_name || 'Unknown City',
                        state: stateComponent?.long_name || 'Unknown State',
                        locality: localityComponent?.long_name,
                        lat: latitude,
                        lng: longitude,
                        formattedAddress: result.formatted_address
                    };

                    setLocation(newLocation);
                }
            } else {
                // Fallback if Google Maps script isn't loaded yet
                console.warn('Google Maps API not loaded, using mock location data');
                setLocation({
                    city: 'Mumbai',
                    state: 'Maharashtra',
                    lat: latitude,
                    lng: longitude,
                    formattedAddress: 'Detected Location (Mock)'
                });
            }

        } catch (error) {
            console.error('Error detecting location:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <LocationContext.Provider value={{ location, setLocation, isLoading, detectLocation }}>
            {children}
        </LocationContext.Provider>
    );
};

export const useLocationContext = () => {
    const context = useContext(LocationContext);
    if (context === undefined) {
        throw new Error('useLocationContext must be used within a LocationProvider');
    }
    return context;
};
